'use client';

import { useState } from 'react';
import { createClient } from '@/lib/supabase/client';

export default function EmailSignup() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [expanded, setExpanded] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setStatus('loading');

    try {
      const supabase = createClient();
      const { error } = await supabase
        .from('email_signups')
        .insert({ email: email.trim().toLowerCase() });

      // Treat unique violation (already subscribed) as success
      if (error && error.code !== '23505') {
        setStatus('error');
        return;
      }

      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '18px',
        left: '18px',
        zIndex: 60,
        maxWidth: '240px',
        fontFamily: "'Fraunces', Georgia, serif",
      }}
    >
      {!expanded && status !== 'success' ? (
        // Collapsed pill — just a subtle label
        <button
          onClick={() => setExpanded(true)}
          style={{
            background: 'rgba(245, 239, 230, 0.85)',
            border: '1px solid rgba(180, 155, 130, 0.3)',
            borderRadius: '999px',
            padding: '6px 14px',
            fontSize: '11px',
            fontWeight: 400,
            fontStyle: 'italic',
            color: 'rgba(140, 110, 95, 0.75)',
            cursor: 'pointer',
            letterSpacing: '0.04em',
            backdropFilter: 'blur(4px)',
            transition: 'opacity 0.2s ease',
            fontFamily: "'Fraunces', Georgia, serif",
          }}
        >
          For parents ✉
        </button>
      ) : status === 'success' ? (
        // Success state
        <div
          style={{
            background: 'rgba(245, 239, 230, 0.9)',
            border: '1px solid rgba(180, 155, 130, 0.25)',
            borderRadius: '16px',
            padding: '12px 16px',
            backdropFilter: 'blur(6px)',
          }}
        >
          <p
            style={{
              fontSize: '12px',
              color: 'rgba(110, 140, 100, 0.9)',
              fontStyle: 'italic',
              fontWeight: 300,
              margin: 0,
            }}
          >
            Thank you! We&apos;ll keep you updated.
          </p>
        </div>
      ) : (
        // Expanded form
        <div
          style={{
            background: 'rgba(245, 239, 230, 0.92)',
            border: '1px solid rgba(180, 155, 130, 0.25)',
            borderRadius: '16px',
            padding: '14px 16px 16px',
            backdropFilter: 'blur(6px)',
            boxShadow: '0 2px 12px rgba(140, 110, 95, 0.08)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
            <p
              style={{
                fontSize: '10px',
                fontWeight: 500,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'rgba(140, 110, 95, 0.7)',
                margin: 0,
              }}
            >
              For parents
            </p>
            <button
              onClick={() => setExpanded(false)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'rgba(140, 110, 95, 0.5)',
                fontSize: '14px',
                lineHeight: 1,
                padding: '0 0 0 8px',
                fontFamily: 'sans-serif',
              }}
              aria-label="Close"
            >
              ×
            </button>
          </div>

          <p
            style={{
              fontSize: '12px',
              color: 'rgba(120, 90, 75, 0.8)',
              marginBottom: '12px',
              fontWeight: 300,
              fontStyle: 'italic',
              lineHeight: 1.4,
              marginTop: '4px',
            }}
          >
            Get notified when we release new worlds and features.
          </p>

          <form
            onSubmit={handleSubmit}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}
          >
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              disabled={status === 'loading'}
              style={{
                padding: '8px 14px',
                borderRadius: '999px',
                border: '1px solid rgba(180, 155, 130, 0.4)',
                background: 'rgba(255, 252, 248, 0.9)',
                fontSize: '12px',
                color: 'rgba(100, 75, 60, 0.9)',
                outline: 'none',
                width: '100%',
                boxSizing: 'border-box',
                fontFamily: "'Fraunces', Georgia, serif",
              }}
            />
            <button
              type="submit"
              disabled={status === 'loading'}
              style={{
                padding: '8px 0',
                borderRadius: '999px',
                border: 'none',
                background: status === 'loading' ? 'rgba(160, 185, 150, 0.5)' : 'rgba(160, 185, 150, 0.75)',
                color: 'rgba(60, 80, 55, 0.9)',
                fontSize: '12px',
                fontWeight: 400,
                cursor: status === 'loading' ? 'default' : 'pointer',
                fontFamily: "'Fraunces', Georgia, serif",
                letterSpacing: '0.03em',
                transition: 'background 0.2s ease',
                width: '100%',
              }}
            >
              {status === 'loading' ? 'Subscribing…' : 'Subscribe'}
            </button>
          </form>

          {status === 'error' && (
            <p
              style={{
                marginTop: '8px',
                fontSize: '11px',
                color: 'rgba(180, 100, 80, 0.8)',
                fontStyle: 'italic',
                margin: '8px 0 0',
              }}
            >
              Something went wrong, please try again.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
